import { postJSON } from "../api.js";
import { cinemaMomentIntro, cinemaMomentReveal } from "../ui/cinema.js";
import { esc } from "../ui/format.js";
import { uiEmpty, uiGet, uiMoney, uiPays } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Signature Moments (backend/signature_moments.py), inside the CEO Office.
 *
 * When this industry's big event is on: a stage card (what's at stake, which
 * of your stats sways the odds), three choices from bold to safe with their
 * odds, cost and what happens either way. Choosing is a real action
 * (execute_action "signature_moment"); the result plays as a cinematic: the
 * reveal number rolls up, then the verdict. The first time you open a new
 * moment, a short intro plays. Otherwise: when it's next, and past results.
 */
const MO_SEEN = "ges.moments.seen";

function moOdds(o) {
  const r = 15, c = 2 * Math.PI * r, col = o.odds >= 70 ? "var(--sage)" : o.odds >= 45 ? "var(--neon-gold)" : "var(--oxblood)";
  return `<svg class="mo-odds" viewBox="0 0 40 40" aria-label="${o.odds}% chance"><circle cx="20" cy="20" r="${r}" class="mo-odds-t"/>
    <circle cx="20" cy="20" r="${r}" stroke="${col}" class="mo-odds-f" stroke-dasharray="${((c * o.odds) / 100).toFixed(1)} ${c.toFixed(1)}" transform="rotate(-90 20 20)"/>
    <text x="20" y="24" text-anchor="middle">${o.odds}%</text></svg>`;
}

function moHistory(list) {
  if (!list.length) return "";
  return `<div class="mo-history"><h4>Past moments</h4>${list.map((h) => `
    <div class="mo-h ${h.success ? "win" : "lose"}"><b>M${h.month}</b><span>${esc(h.choice)}${h.decided_by !== "you" ? ` <em>(${esc(h.decided_by)})</em>` : ""}</span>
      <strong>${h.reveal.value == null ? "—" : `${(+h.reveal.value).toLocaleString("en-US", { maximumFractionDigits: h.reveal.decimals })}${esc(h.reveal.unit)}`}</strong><small>${esc(h.text)}</small></div>`).join("")}</div>`;
}

async function renderMomentsInto(host) {
  const g = await uiGet("/moments");
  const m = g.moment, cur = g.current;
  if (!cur) {
    host.innerHTML = `<div class="mo">
      <div class="mo-hero mo-quiet"><span class="mo-icon">${esc(m.icon)}</span><div><div class="mo-kick">Your industry's Signature Moment</div><h3>${esc(m.title)}</h3><p>${esc(m.tagline)}</p></div></div>
      ${uiEmpty(`Next ${m.title}: month ${g.next_month}`, `${g.rules} Launching a product brings it sooner.`)}
      ${moHistory(g.history)}</div>`;
    return;
  }
  const ctx = cur.context || {};
  host.innerHTML = `<div class="mo">
    <div class="mo-hero"><span class="mo-icon">${esc(m.icon)}</span>
      <div><div class="mo-kick">${ctx.reason === "launch" ? `Your launch of ${esc(ctx.product || "a new product")} flows into` : `Month ${cur.month} · it's on`}</div>
        <h3>${esc(m.title)}</h3><p>${esc(m.tagline)}</p>
        <div class="mo-driver">Odds ride on <b>${esc(cur.driver.label)}</b>: <span class="mo-bar"><i style="width:${cur.driver.value}%"></i></span> <b>${Math.round(cur.driver.value)}</b>/100</div></div></div>
    <div class="mo-choices">${cur.choices.map((o) => `
      <button type="button" class="mo-choice mo-${o.key}" data-mo="${o.key}" ${o.affordable ? "" : "disabled"}>
        ${moOdds(o)}
        <div class="mo-c-body"><b>${esc(o.label)}</b><span>${esc(o.detail)}</span>
          <div class="mo-c-fx"><span class="up">If it works: ${esc(o.upside)}</span>${o.downside ? `<span class="down">If not: ${esc(o.downside)}</span>` : ""}</div></div>
        <em>${o.cost ? uiMoney(o.cost) : "Free"}${o.affordable ? "" : " · can't afford"}</em>${uiPays(o)}</button>`).join("")}</div>
    <p class="mo-note">${esc(g.rules)}</p>
    ${moHistory(g.history)}</div>`;
  let seen = [];
  try { seen = JSON.parse(localStorage.getItem(MO_SEEN) || "[]"); } catch (e) { /* first time */ }
  if (!seen.includes(cur.id)) {
    try { localStorage.setItem(MO_SEEN, JSON.stringify([...seen, cur.id].slice(-20))); } catch (e) { /* not remembered */ }
    cinemaMomentIntro(m, cur);
  }
  host.querySelectorAll("[data-mo]").forEach((b) => b.addEventListener("click", async () => {
    host.querySelectorAll("[data-mo]").forEach((x) => { x.disabled = true; });
    try {
      await postJSON("/actions/execute", { action: { type: "signature_moment", params: { choice: b.dataset.mo } } });
      const after = await uiGet("/moments");
      if (after.history[0]) await cinemaMomentReveal(after.history[0], m);
    } catch (e) { toast(e.message || "That didn't work.", true); }
    await window.refreshAll();
  }));
}

window.renderMomentsInto = renderMomentsInto;

export { renderMomentsInto };
